import { useCallback, useEffect, useState } from 'react';
import { Title, useCreatePath } from 'react-admin';
import { Link, useLocation } from 'react-router-dom';
import { Alert, Box, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import {
  FEATURE_ORDER_CHANGE_REGISTER,
  ORDER_CHANGE_STATUS_FILTERS,
  fetchOrderChangeList,
  toErrorMessage,
  type OrderChange,
} from './orderChangesApi';
import { OrderChangeItem } from './OrderChangeItem';

/**
 * „Zmiany zleceń” — lista zmian „było → ma być” dla przyjęcia zleceń
 * (makieta back office v2, fala B, B9; przełącznik `orderChangeRegister`).
 * Domyślnie zmiany w toku; filtr stanu i „TMS pokazuje inną wartość”
 * (przypomnienie po imporcie). Czynności te same co na karcie zlecenia.
 * Filtr przychodzi też z adresu (`?filter={"status":…}`) — kafelki pulpitu
 * przyjęcia zleceń liczą tym samym warunkiem.
 */

export const FEATURE_OFF_NOTE = 'Rejestr zmian zleceń jest wyłączony w wersji aplikacji, z której korzystasz.';
export const NO_PERMISSION_NOTE = 'Lista zmian zleceń jest dla przyjęcia zleceń (CS i Administracja).';

/** Filtr z adresu listy (jak w react-admin): `?filter={"status":"pending"}`. */
export function filterFromSearch(search: string): { status: string; reminder: boolean } {
  try {
    const raw = new URLSearchParams(search).get('filter');
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    return {
      status: typeof parsed.status === 'string' ? parsed.status : 'open',
      reminder: parsed.reminder === true,
    };
  } catch {
    return { status: 'open', reminder: false };
  }
}

export const OrderChangeList = () => {
  const on = useFeatureState(FEATURE_ORDER_CHANGE_REGISTER);
  const allowed = useHasPermission('ORDER_INTAKE');
  if (on === undefined) return <FeaturesLoading />;
  if (!on || !allowed) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel('order-changes')} />
        <Alert severity="info">{on ? NO_PERMISSION_NOTE : FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <ListScreen />;
};

const ListScreen = () => {
  const location = useLocation();
  const createPath = useCreatePath();
  const initial = filterFromSearch(location.search);
  const [status, setStatus] = useState(initial.status);
  const [reminder, setReminder] = useState(initial.reminder);
  const [items, setItems] = useState<OrderChange[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(await fetchOrderChangeList(reminder ? { reminder: true } : { status }));
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [status, reminder]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack spacing={2} sx={{ p: 2 }} data-testid="order-change-list">
      <Title title={menuLabel('order-changes')} />
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Stan"
          value={reminder ? 'reminder' : status}
          onChange={(e) => {
            if (e.target.value === 'reminder') {
              setReminder(true);
            } else {
              setReminder(false);
              setStatus(e.target.value);
            }
          }}
          sx={{ minWidth: 260 }}
        >
          {ORDER_CHANGE_STATUS_FILTERS.map((f) => (
            <MenuItem key={f.id} value={f.id}>
              {f.name}
            </MenuItem>
          ))}
          <MenuItem value="reminder">TMS pokazuje inną wartość (przypomnienie)</MenuItem>
        </TextField>
        <Typography variant="caption" color="text.secondary">
          Do TMS nic nie wychodzi z aplikacji — zmianę wpisuje przyjęcie zleceń, zamyka import.
        </Typography>
      </Stack>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {items === null && !error ? <CircularProgress size={24} /> : null}
      {items && items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak zmian w tym widoku.
        </Typography>
      ) : null}
      {items?.map((c) => (
        <Box key={c.id}>
          <Link to={createPath({ resource: 'user-offers', type: 'show', id: c.userOfferId })}>
            {c.orderNumber ?? `Zlecenie #${c.userOfferId}`}
          </Link>
          <OrderChangeItem change={c} onChanged={() => void load()} />
        </Box>
      ))}
    </Stack>
  );
};
